import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpToggle } from '@/components/common/HelpToggle';
import { EmptyState } from '@/components/common/StateViews';
import { guidesCollapsed } from '@/lib/guideDisplay';
import type { AnswerOut } from '@/services/types';
import { cn } from '@/lib/cn';

/**
 * Renders an analytical answer, or its refusal.
 *
 * This is the frontend half of the substrate contract. Two rules it enforces so
 * that no individual screen has to remember them:
 *
 * 1. Disclosure keys off the `substrate` CODE, never off the prose in `reason`.
 *    The sentence is for the reader; the code is for the UI.
 * 2. A refusal renders as a stated absence, never as an empty chart or a zero.
 *    "ANATEL has no decision-maker data" and "no decisions matched" are
 *    different facts and must not look the same.
 */
/**
 * The refusal sentence, in the reader's language.
 *
 * A refusal is the deliverable on several screens -- "this cannot be answered,
 * and here is the denominator" is what a lawyer acts on -- so it has to be
 * readable. The backend authors it in English and, where a translation exists,
 * also sends `reason_key` plus the MEASURED numbers in `reason_params`.
 *
 * `reason` remains the fallback, deliberately. A refusal that has not been
 * translated yet must still say something true rather than render a bare key,
 * and an untranslated English sentence is far better than silence on a screen
 * whose entire purpose is to explain an absence.
 *
 * The numbers arrive as parameters rather than baked into the sentence so the
 * three languages cannot drift from each other or from the measurement.
 */
function useReasonText<T>(answer: AnswerOut<T>): string | undefined {
  const { t, i18n } = useTranslation();
  if (!answer.reason_key) return answer.reason ?? undefined;
  if (!i18n.exists(answer.reason_key)) return answer.reason ?? undefined;
  return t(answer.reason_key, { ...answer.reason_params });
}

/**
 * What a refusal is ABOUT.
 *
 * `corpus` is the default and the common case: the agency's data cannot support
 * the analysis at all. `record` means the corpus supports it fine and this
 * particular subject is absent from it -- a company with no infraction notice,
 * for instance.
 *
 * They share a substrate code and must not share a label. Rendering "unavailable
 * for this agency" over "this company has no infraction notice" tells a reader
 * ANEEL cannot do enforcement analysis, on the strength of one company never
 * having been fined. That confusion has now appeared three times in this
 * product, which is why it is a prop rather than a rule to remember.
 */
export type RefusalScope = 'corpus' | 'record';

export function Answer<T>({
  answer,
  children,
  help,
  scope = 'corpus',
  elsewhere,
}: {
  answer: AnswerOut<T>;
  children: (value: T) => ReactNode;
  help?: string;
  scope?: RefusalScope;
  /**
   * Where the numbers ARE, when the refusal withholds only one RENDERING of
   * them.
   *
   * `EmptyState` is right when the answer does not exist -- ANATEL has no
   * decision-maker data, and a stated absence is the finding. It is wrong when
   * the data exists, is displayed on the same screen, and only a particular
   * presentation of it was declined.
   *
   * Measured on `/cade/activity`: the fine series is refused as a TREND, because
   * 215 of 8,107 processes carry an amount and one cartel settlement moves a
   * year by billions. The yearly totals are nonetheless real and are printed in
   * the table below, beside the count each rests on. Rendering that refusal as
   * "nothing to show" put an empty inbox directly above a field guide reading
   * "each year's total appears in the table -- read the two together". The card
   * contradicted its own explanation, and a reader had no way to tell which half
   * was wrong.
   *
   * Same family as `scope` above, on a different axis: `scope` separates "the
   * corpus cannot" from "this subject is absent", and this separates both from
   * "shown, deliberately, as something else".
   */
  elsewhere?: ReactNode;
}) {
  const reason = useReasonText(answer);
  const shown = answer.substrate === 'available' || answer.substrate === 'partial';

  if (!shown) {
    // A refusal is NOT an empty state, and rendering it as one says the opposite
    // of what this component exists to say. `EmptyState` leads with an inbox icon
    // and the words "nothing to show" -- correct for a filter that matched no
    // rows, and false here, where the card carries a measured explanation with
    // its denominators in it. Three cards on `/cade/overview` read "Nada a
    // exibir" directly above sixty words of measurement.
    //
    // The refusal keeps the dashed border, because a stated absence given the
    // same visual weight as a figure is the whole point -- what goes is the
    // heading that contradicted the paragraph beneath it.
    return (
      <div className="space-y-2">
        <SubstrateBadge substrate={answer.substrate} scope={scope} />
        {reason || elsewhere ? (
          <div className="space-y-2 rounded-md border border-dashed p-3 text-xs">
            {reason ? <p className="text-muted-foreground">{reason}</p> : null}
            {elsewhere ? (
              <p className="flex items-start gap-1.5 text-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <span>{elsewhere}</span>
              </p>
            ) : null}
          </div>
        ) : (
          // Only when there is genuinely nothing to say -- a refusal with no
          // reason, which the backend should never emit and which would
          // otherwise render as a bare badge.
          <EmptyState />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {children(answer.value as T)}
      <PopulationNote
        answer={answer}
        help={help}
        // A PARTIAL answer that names a translation is one whose author had
        // something specific to say about the gap (which rows are outside the
        // figure and why), not the generic coverage note `answered()` writes.
        // Rendered only when the KEY exists: without it `reason` is the
        // backend's English coverage sentence, which the i18n boundary keeps
        // off the screen. Until 2026-09-18 this sentence was computed on every
        // request and never shown -- the ANS board-date gap and the IBAMA
        // person and currency fences all shipped their measured disclosure
        // into a field nothing read.
        reasonText={answer.substrate === 'partial' && answer.reason_key ? reason : undefined}
      />
    </div>
  );
}

/**
 * A panel whose whole content is an absence.
 *
 * Given the same visual weight as a figure, because "this cannot be answered,
 * and here is the denominator" is a finding a lawyer can act on, while a missing
 * panel is indistinguishable from one nobody built.
 *
 * Renders nothing if the substrate ever becomes available -- these panels exist
 * to explain an absence, and a value appearing here means the analysis graduated
 * and deserves a real panel of its own.
 */
export function RefusalCard<T>({
  title,
  answer,
  scope = 'corpus',
  elsewhere,
}: {
  title: string;
  answer: AnswerOut<T>;
  /**
   * Same axis as `Answer`'s, and it was missing here until 2026-09-10.
   *
   * `RefusalCard` took no `scope` at all and called `Answer` without one, so it
   * was permanently `corpus` -- meaning a DETAIL page could not express "this
   * subject is absent" through a refusal card at any price, and would announce
   * that the whole agency lacks the analysis instead. `corpus` stays the default
   * because every card built before this prop existed is a corpus-level refusal
   * and must not change meaning.
   */
  scope?: RefusalScope;
  /** Where the underlying rows ARE, when this card withholds only a summary. */
  elsewhere?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-2 text-sm font-medium text-foreground">{title}</h3>
      <Answer answer={answer} scope={scope} elsewhere={elsewhere}>
        {() => null}
      </Answer>
    </div>
  );
}

export function SubstrateBadge({
  substrate,
  scope = 'corpus',
}: {
  substrate: string;
  scope?: RefusalScope;
}) {
  const { t } = useTranslation();
  // `record` labels exist only for the codes that can describe an absent
  // subject; anything else falls back to the corpus wording rather than
  // rendering a bare key.
  const scoped = `substrate.absent.${substrate}`;
  const key = scope === 'record' && t(scoped) !== scoped ? scoped : `substrate.${substrate}`;
  const label = t(key);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium',
        substrate === 'partial'
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          : 'border-muted-foreground/30 bg-muted text-muted-foreground',
      )}
    >
      {label === key ? substrate : label}
    </span>
  );
}

/**
 * The "no orphan numbers" footer: every figure states the population it was
 * computed over, its denominator, and how much of that denominator carried a
 * value.
 */
function PopulationNote<T>({
  answer,
  help,
  reasonText,
}: {
  answer: AnswerOut<T>;
  help?: string;
  reasonText?: string;
}) {
  const { t, i18n } = useTranslation();
  const { population } = answer;
  const coverage = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(population.coverage);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
      {answer.substrate === 'partial' ? (
        <div className="flex items-center gap-2">
          <SubstrateBadge substrate={answer.substrate} />
          <span>
            {t('substrate.measuredOf', {
              measured: number.format(population.measured),
              total: number.format(population.total),
              coverage,
            })}
          </span>
        </div>
      ) : null}
      {reasonText ? (
        <p className="rounded-md border border-dashed p-2" data-partial-reason>
          {reasonText}
        </p>
      ) : null}
      {/* The i18n boundary. `answer.notes` and `population.described` arrive from
          the backend as ENGLISH prose and are deliberately NOT rendered: all
          user-visible text lives in the locale files, and showing both produced
          each caveat twice, once per language. They stay in the payload because
          an export and an audit trail need the machine's own words -- but the
          screen speaks the user's.

          This is a stopgap. The proper fix is for the backend to emit note CODES
          plus parameters that the frontend translates, so a caveat cannot exist
          in one language only. Until then a note without a locale entry is
          invisible here, which is why `help` must cover every case the backend
          can emit. */}
      {/* On screens whose guides collapse (CADE, lib/guideDisplay.ts), the reading note goes
          behind the same help icon: the figure and its population stay in view, the
          explanation is one click away. */}
      {help && guidesCollapsed() ? (
        <HelpToggle title={t('substrate.howToRead')}>
          <p className="text-xs text-muted-foreground">{help}</p>
        </HelpToggle>
      ) : help ? (
        <p className="flex items-start gap-1.5">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{help}</span>
        </p>
      ) : null}
    </div>
  );
}
